import type { IntlShape } from 'react-intl';

import type { IRobloxSupportFeature } from '../types';

export const ROBLOX_SUPPORT_FEATURE_I18N_KEY_MAP: Record<
  keyof IRobloxSupportFeature,
  string
> = {
  voice_chat: 'dbi_roblox_support_feature_voice_chat',
  camera: 'dbi_roblox_support_feature_camera',
};

export const ROBLOX_SUPPORT_FEATURE_EN_LABEL_MAP: Record<
  keyof IRobloxSupportFeature,
  string
> = {
  voice_chat: 'Voice Chat',
  camera: 'Camera',
};

export const normalizeRobloxSupportFeature = (
  supportFeature: IRobloxSupportFeature | string | null | undefined,
): IRobloxSupportFeature | null => {
  if (!supportFeature) {
    return null;
  }

  if (typeof supportFeature === 'object') {
    return supportFeature;
  }

  if (typeof supportFeature !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(supportFeature);

    if (parsed && typeof parsed === 'object') {
      return parsed as IRobloxSupportFeature;
    }
  } catch (error) {
    return null;
  }

  return null;
};

const formatRobloxSupportFeatureLabels = (
  supportFeature: IRobloxSupportFeature | string | null | undefined,
  getLabel: (featureKey: keyof IRobloxSupportFeature) => string,
): string => {
  const normalizedSupportFeature = normalizeRobloxSupportFeature(supportFeature);

  if (!normalizedSupportFeature) {
    return '';
  }

  return (Object.keys(ROBLOX_SUPPORT_FEATURE_EN_LABEL_MAP) as Array<
    keyof IRobloxSupportFeature
  >)
    .filter((featureKey) => normalizedSupportFeature[featureKey] === true)
    .map((featureKey) => getLabel(featureKey))
    .join(', ');
};

export const formatRobloxSupportFeatureEnText = (
  supportFeature: IRobloxSupportFeature | string | null | undefined,
): string => {
  return formatRobloxSupportFeatureLabels(
    supportFeature,
    (featureKey) => ROBLOX_SUPPORT_FEATURE_EN_LABEL_MAP[featureKey],
  );
};

export const formatRobloxSupportFeatureText = (
  supportFeature: IRobloxSupportFeature | string | null | undefined,
  formatMessage: IntlShape['formatMessage'],
): string => {
  return formatRobloxSupportFeatureLabels(
    supportFeature,
    (featureKey) => formatMessage({ id: ROBLOX_SUPPORT_FEATURE_I18N_KEY_MAP[featureKey] }),
  );
};
